import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface OgrenciTP {
  id: string;
  ad: string;
  soyad: string;
  email: string | null;
  bakiye: number;
}

interface Hareket {
  id: string;
  tutar: number;
  tip: 'kazanim' | 'harcama';
  islem_tipi: string;
  aciklama: string | null;
  olusturuldu: string;
}

function formatTarih(t: string) {
  return new Date(t).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function TPListesi() {
  usePageTitle('TP Listesi');
  const { token } = useAuth() as any;
  const { isDark } = useTheme();
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const card = isDark ? '#1a1a1a' : 'white';
  const cardAlt = isDark ? '#1e1e1e' : '#f5f5f5';
  const border = isDark ? '#2a2a2a' : '#eee';
  const borderSub = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';

  const [liste, setListe] = useState<OgrenciTP[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [arama, setArama] = useState('');
  const [secilen, setSecilen] = useState<OgrenciTP | null>(null);
  const [hareketler, setHareketler] = useState<Hareket[]>([]);
  const [hareketYukleniyor, setHareketYukleniyor] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/api/tp/liste`, { headers: h })
      .then(r => r.json())
      .then(setListe)
      .finally(() => setYukleniyor(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function detayAc(o: OgrenciTP) {
    setSecilen(o);
    setHareketYukleniyor(true);
    const r = await fetch(`${BASE_URL}/api/tp/gecmis?ogrenci_id=${o.id}`, { headers: h });
    setHareketler(await r.json());
    setHareketYukleniyor(false);
  }

  const filtreli = liste.filter(o =>
    `${o.ad} ${o.soyad} ${o.email || ''}`.toLowerCase().includes(arama.toLowerCase())
  );

  const td: React.CSSProperties = { padding: '11px 14px', fontSize: 14, verticalAlign: 'middle', color: textPrimary };

  return (
    <div style={{ padding: 32, maxWidth: 800 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: textPrimary }}>TP Listesi</h2>
        <p style={{ margin: '4px 0 0', color: textSec, fontSize: 13 }}>
          TP kullanmaya hakkı olan (mobil uygulama kullanıcısı) öğrenciler — isme tıklayınca kazanım/harcama geçmişi görünür
        </p>
      </div>

      <input
        value={arama} onChange={e => setArama(e.target.value)}
        placeholder="Ad, soyad veya e-posta ara..."
        style={{ width: '100%', maxWidth: 320, padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, fontSize: 13, marginBottom: 16, outline: 'none', background: isDark ? '#111' : 'white', color: textPrimary, boxSizing: 'border-box' }}
      />

      {yukleniyor ? (
        <p style={{ color: textMuted }}>Yükleniyor…</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: card, borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <thead>
            <tr style={{ background: cardAlt, fontSize: 12, color: textSec, textTransform: 'uppercase' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>Öğrenci</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>E-posta</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600 }}>TP Bakiyesi</th>
            </tr>
          </thead>
          <tbody>
            {filtreli.map((o, i) => (
              <tr key={o.id} onClick={() => detayAc(o)}
                style={{ borderTop: i ? `1px solid ${borderSub}` : undefined, cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = cardAlt)}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <td style={td}><span style={{ fontWeight: 600 }}>{o.ad} {o.soyad}</span></td>
                <td style={{ ...td, color: textSec, fontSize: 13 }}>{o.email || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <span style={{ fontWeight: 700, color: o.bakiye > 0 ? '#3C3489' : textMuted }}>⭐ {o.bakiye} TP</span>
                </td>
              </tr>
            ))}
            {!filtreli.length && (
              <tr><td colSpan={3} style={{ padding: 24, textAlign: 'center', color: textMuted }}>Sonuç bulunamadı</td></tr>
            )}
          </tbody>
        </table>
      )}

      {secilen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setSecilen(null)}>
          <div style={{ background: card, borderRadius: 14, padding: 26, width: 480, maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <h3 style={{ margin: 0, fontSize: 17, color: textPrimary }}>{secilen.ad} {secilen.soyad}</h3>
              <button onClick={() => setSecilen(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: textMuted }}>×</button>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#3C3489', marginBottom: 18 }}>⭐ {secilen.bakiye} TP mevcut bakiye</div>

            {hareketYukleniyor ? (
              <p style={{ color: textMuted, fontSize: 13 }}>Yükleniyor…</p>
            ) : hareketler.length === 0 ? (
              <p style={{ color: textMuted, fontSize: 13 }}>Henüz TP hareketi yok</p>
            ) : (
              hareketler.map(hk => (
                <div key={hk.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: `1px solid ${borderSub}` }}>
                  <div>
                    <div style={{ fontSize: 13, color: textPrimary, fontWeight: 500 }}>{hk.aciklama || hk.islem_tipi}</div>
                    <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{formatTarih(hk.olusturuldu)}</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: hk.tip === 'kazanim' ? '#16a34a' : '#dc2626' }}>
                    {hk.tip === 'kazanim' ? '+' : '-'}{hk.tutar} TP
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
